import type { ExpandedState } from '@tanstack/react-table';

/**
 * ExpandedStateが Record<string, boolean> 型かどうかを判定
 */
export const isExpandedStateObject = (
  state: ExpandedState | undefined,
): state is Record<string, boolean> => {
  if (!state || state === true) return false;
  return typeof state === 'object';
};

/**
 * ExpandedStateから特定の行の展開状態を取得
 *
 * @param state - 展開状態
 * @param rowId - 行ID
 * @returns 展開されている場合true
 */
export const getExpandedValue = (state: ExpandedState | undefined, rowId: string): boolean => {
  if (!state) return false;
  if (state === true) return true;
  return isExpandedStateObject(state) ? (state[rowId] ?? false) : false;
};

/**
 * ExpandedStateに特定の行の展開状態を設定
 *
 * @param state - 現在の展開状態
 * @param rowId - 行ID
 * @param value - 展開する場合true、閉じる場合false
 * @returns 新しい展開状態
 */
export const setExpandedValue = (
  state: ExpandedState | undefined,
  rowIds: string[],
  value: boolean,
): ExpandedState => {
  const currentState = isExpandedStateObject(state) ? state : {};
  if (value) {
    return {
      ...currentState,
      ...rowIds.reduce<Record<string, boolean>>((acc, id) => {
        acc[id] = true;
        return acc;
      }, {}),
    };
  }
  return {
    ...currentState,
    ...rowIds.reduce<Record<string, boolean>>((acc, id) => {
      acc[id] = false;
      return acc;
    }, {}),
  };
};

/**
 * ExpandedStateから特定の行IDを削除（他の展開状態は維持）
 *
 * @param state - 現在の展開状態
 * @param rowIds - 削除する行IDの配列
 * @returns 新しい展開状態
 */
export const removeExpandedValues = (
  state: ExpandedState | undefined,
  rowIds: string[],
): ExpandedState => {
  if (!isExpandedStateObject(state)) return {};

  const newState = { ...state };
  for (const id of rowIds) {
    delete newState[id];
  }
  return newState;
};
